import { useTranslation } from "react-i18next";
import { Link } from "@/lib/link";
import { BookOpen, FileText, Globe2, Calculator, Hash, ClipboardCheck, ArrowRight } from "lucide-react";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import ScrollReveal from "@/components/common/ScrollReveal";
import SectionHeading from "@/components/common/SectionHeading";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { faqs } from "@/data/faqs";

const resources = [
    { Icon: BookOpen, tKey: "resourcesPage.resources.blog.title", dKey: "resourcesPage.resources.blog.description", tagKey: "resourcesPage.resources.blog.tag" },
    { Icon: FileText, tKey: "resourcesPage.resources.guides.title", dKey: "resourcesPage.resources.guides.description", tagKey: "resourcesPage.resources.guides.tag" },
    { Icon: Globe2, tKey: "resourcesPage.resources.countryReports.title", dKey: "resourcesPage.resources.countryReports.description", tagKey: "resourcesPage.resources.countryReports.tag" },
    { Icon: Hash, tKey: "resourcesPage.resources.hsCode.title", dKey: "resourcesPage.resources.hsCode.description", tagKey: "resourcesPage.resources.hsCode.tag" },
    { Icon: Calculator, tKey: "resourcesPage.resources.calculators.title", dKey: "resourcesPage.resources.calculators.description", tagKey: "resourcesPage.resources.calculators.tag" },
    { Icon: ClipboardCheck, tKey: "resourcesPage.resources.templates.title", dKey: "resourcesPage.resources.templates.description", tagKey: "resourcesPage.resources.templates.tag" },
];

const blogArticles = [
    {
        title: "How to source export-quality hotel linen from India",
        description: "A practical guide to sampling, quality checks, and supplier communication for hospitality buyers.",
        category: "Sourcing",
        readTime: "6 min read",
    },
    {
        title: "Export compliance checklist for textile and safety shipments",
        description: "Learn what exporters should verify before quoting, packing, and dispatching cross-border orders.",
        category: "Compliance",
        readTime: "4 min read",
    },
    {
        title: "Private label sourcing for retail and e-commerce brands",
        description: "From fabric selection to packaging and branding, here is how teams scale private-label imports.",
        category: "Private label",
        readTime: "5 min read",
    },
];

export default function Resources() {
    const { t } = useTranslation();
    return (
        <>
            <SEO
                title="Export Resources, Guides, HS Codes & Calculators | Exportfy.world"
                description="Export guides, country reports, HS code finder, export calculators, and downloadable templates for exporters and importers."
                path="/resources"
            />
            <PageHero
                eyebrow={t("resourcesPage.hero.eyebrow")}
                title={t("resourcesPage.hero.title")}
                subtitle={t("resourcesPage.hero.subtitle")}
            />

            <section className="section-y">
                <div className="container-x">
                    <ScrollReveal><SectionHeading eyebrow={t("resourcesPage.explore.eyebrow")} title={t("resourcesPage.explore.title")} /></ScrollReveal>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {resources.map(({ Icon, tKey, dKey, tagKey }, i) => (
                            <ScrollReveal key={tKey} delay={i * 40}>
                                <div className="group bg-white border border-slate-200 rounded-2xl p-7 h-full hover:shadow-[0_20px_40px_rgba(10,37,64,0.08)] hover:-translate-y-0.5 transition-all">
                                    <div className="flex items-start justify-between">
                                        <div className="w-11 h-11 rounded-xl bg-[#0A2540] flex items-center justify-center text-white"><Icon className="w-5 h-5" strokeWidth={1.75} /></div>
                                        <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-700 bg-amber-50 rounded-full px-2.5 py-1">{t(tagKey)}</span>
                                    </div>
                                    <div className="mt-5 font-bold text-lg text-[#0A2540]">{t(tKey)}</div>
                                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">{t(dKey)}</p>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section-y">
                <div className="container-x">
                    <ScrollReveal><SectionHeading eyebrow="Blog & articles" title="Export insights for sourcing, compliance, and growth" /></ScrollReveal>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {blogArticles.map((article, i) => (
                            <ScrollReveal key={article.title} delay={i * 40}>
                                <div className="group h-full rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(10,37,64,0.08)]">
                                    <div className="inline-flex items-center rounded-full bg-[#DBEAFE] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#1D4ED8]">
                                        {article.category}
                                    </div>
                                    <h3 className="mt-4 text-xl font-bold text-[#0A2540]">{article.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{article.description}</p>
                                    <div className="mt-6 flex items-center justify-between text-sm font-semibold text-[#1D4ED8]">
                                        <span>{article.readTime}</span>
                                        <span className="inline-flex items-center gap-2">Read article <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
                                    </div>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section-y bg-[#F8FAFC]">
                <div className="container-x max-w-4xl">
                    <ScrollReveal><SectionHeading eyebrow={t("resourcesPage.faq.eyebrow")} title={t("resourcesPage.faq.title")} /></ScrollReveal>
                    <Accordion type="single" collapsible className="mt-10 space-y-3">
                        {faqs.map((f, i) => (
                            <AccordionItem key={i} value={`item-${i}`} className="border border-slate-200 bg-white rounded-2xl px-6 data-[state=open]:bg-slate-50">
                                <AccordionTrigger className="text-left font-bold text-[#0A2540] hover:no-underline">{f.q}</AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed pb-6">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>
        </>
    );
}
